import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { Id } from '@shared/types';

/**
 * Multi-select for bulk actions.
 *
 * Deliberately separate from the share basket: the basket is a persisted
 * collection you assemble over time and hand to someone outside the company,
 * while this is a scratch selection for one action on the current screen.
 * Sharing one store would let a stray "move these" tick silently change what a
 * share link contains.
 *
 * Plain context rather than a state library — the app has none, and this needs
 * only a set and three operations.
 */
interface SelectionApi {
  ids: Set<Id>;
  toggle: (id: Id) => void;
  clear: () => void;
  isSelected: (id: Id) => boolean;
  /** True once anything is selected — drives the bulk toolbar. */
  active: boolean;
}

const SelectionContext = createContext<SelectionApi>({
  ids: new Set(),
  toggle: () => {},
  clear: () => {},
  isSelected: () => false,
  active: false,
});

export function useSelection(): SelectionApi {
  return useContext(SelectionContext);
}

export function SelectionProvider({ children }: { children: React.ReactNode }) {
  const [ids, setIds] = useState<Set<Id>>(() => new Set());

  const toggle = useCallback((id: Id) => {
    setIds((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const clear = useCallback(() => setIds(new Set()), []);

  const api = useMemo<SelectionApi>(
    () => ({
      ids,
      toggle,
      clear,
      isSelected: (id: Id) => ids.has(id),
      active: ids.size > 0,
    }),
    [ids, toggle, clear],
  );

  return <SelectionContext.Provider value={api}>{children}</SelectionContext.Provider>;
}
